/**
 * Utility helpers for the Data Provider layer.
 */

/**
 * Convert a resource name to a WP post_type slug.
 *
 * Rules:
 *  1. camelCase → kebab-case
 *  2. Strip trailing "s" to make singular (naive but effective for our domain)
 *
 * Examples:
 *  - "testimonials"    → "testimonial"
 *  - "serviceAreas"    → "service-area"
 *  - "featuredProjects" → "featured-project"
 *  - "faq"             → "faq"
 *  - "seoMatrix"       → "seo-matrix"
 */
export const toPostType = (resource: string): string => {
  // camelCase → kebab-case
  const kebab = resource
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase();

  // Singularise: strip trailing "s" but keep words ending in "ss" (e.g. "business")
  if (kebab.endsWith('ies')) {
    return kebab.slice(0, -3) + 'y'; // e.g. "categories" → "category"
  }
  if (kebab.endsWith('ses')) {
    return kebab; // e.g. "addresses" — keep as-is (rare in our domain)
  }
  if (kebab.endsWith('s') && !kebab.endsWith('ss')) {
    return kebab.slice(0, -1);
  }
  return kebab;
};

/**
 * Convert a WP option name (kebab-case) to the format expected by the API.
 * Currently a pass-through; customise if WP uses a prefix.
 */
export const toOptionKey = (name: string): string => name;

/**
 * Map common WP post fields → our internal field names.
 * Meta fields are spread at the top level.
 */
export const mapWpPostToRecord = (wpPost: Record<string, unknown>): Record<string, unknown> => {
  const { ID, title, content, excerpt, status, slug, date, modified, meta, ...rest } = wpPost;

  return {
    id: ID ?? (wpPost as any).id,
    title: title ?? (wpPost as any).title,
    content: content ?? (wpPost as any).content,
    excerpt: excerpt ?? (wpPost as any).excerpt,
    status: status ?? (wpPost as any).status,
    slug: slug ?? (wpPost as any).slug,
    created_at: date ?? (wpPost as any).created_at,
    updated_at: modified ?? (wpPost as any).updated_at,
    // Spread meta key/values at top level
    ...(meta && typeof meta === 'object' ? meta as Record<string, unknown> : {}),
    ...rest,
  };
};

/**
 * Map our internal record → WP-style payload for creating/updating posts.
 * Anything that isn't a known WP core field goes into `meta`.
 */
const WP_CORE_FIELDS = new Set([
  'id', 'title', 'content', 'excerpt', 'status', 'slug',
  'created_at', 'updated_at', 'post_type',
]);

/**
 * Derive a fallback title from a record when no explicit `title` field exists.
 * Checks common identifying fields in order of specificity.
 */
const deriveTitle = (record: Record<string, unknown>, postType: string): string => {
  // Prefer explicit title
  if (record.title && typeof record.title === 'string') return record.title;
  // module_visibility
  if (record.module_label && typeof record.module_label === 'string') return record.module_label as string;
  if (record.module_path && typeof record.module_path === 'string') return record.module_path as string;
  // checklist_states
  if (record.item_id && typeof record.item_id === 'string') {
    const type = record.checklist_type ? `${record.checklist_type}-` : '';
    return `${type}${record.item_id}`;
  }
  // generic
  if (record.name && typeof record.name === 'string') return record.name as string;
  if (record.slug && typeof record.slug === 'string') return record.slug as string;
  if (record.id && typeof record.id === 'string') return record.id as string;
  return postType;
};

export const mapRecordToWpPost = (
  record: Record<string, unknown>,
  postType: string,
): Record<string, unknown> => {
  const meta: Record<string, unknown> = {};
  const payload: Record<string, unknown> = { type: postType };

  for (const [key, value] of Object.entries(record)) {
    if (WP_CORE_FIELDS.has(key)) {
      // Map our names → WP names
      switch (key) {
        case 'title': payload.title = value; break;
        case 'content': payload.content = value; break;
        case 'excerpt': payload.excerpt = value; break;
        case 'status': payload.status = value; break;
        case 'slug': payload.slug = value; break;
        default: break; // id, dates — server-managed
      }
    } else {
      meta[key] = value;
    }
  }

  // WP requires a title — auto-generate one if not provided
  if (!payload.title) {
    payload.title = deriveTitle(record, postType);
  }

  if (Object.keys(meta).length > 0) {
    payload.meta = meta;
  }

  return payload;
};

